// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { recordUndo } from './history_service.js?v=20260903c';
import {
  closestPointOnSegment,
  distance,
  distancePointToSegment,
  normalizeGeometryRef,
  renderDimensionSnapPreview,
  resolveGeometryRef,
  snapToDimensionGeometry
} from './dimension_service.js?v=20260904f';
import {
  PLAN_LABEL_SCALE_MODES,
  resolvePlanLabelMetrics
} from '../render/plan_label_options.js?v=20260828b';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';

const AREA_MEASUREMENT_TOOL = 'area_measurement';
const AREA_LABEL_REFERENCE_FONT_PX = 13;
const AREA_EDGE_LABEL_REFERENCE_FONT_PX = 11;
const AREA_EDGE_LABEL_FONT_MULTIPLIER = AREA_EDGE_LABEL_REFERENCE_FONT_PX / AREA_LABEL_REFERENCE_FONT_PX;
const AREA_EDGE_LABEL_INSET_REFERENCE_PX = 16;
const MIN_AREA_SQUARE_MM = 1;
const POINT_EQUALITY_TOLERANCE_MM = 0.1;
const VERTEX_DRAG_START_TOLERANCE_PX = 3;
const EDGE_CHAIN_JOIN_TOLERANCE_MM = 2;
const AREA_VIEWPORT_MARGIN_PX = 80;
const AREA_LABEL_MIN_SCREEN_SIZE_PX = 42;
const AREA_EDGE_LABEL_MIN_SCREEN_LENGTH_PX = 72;

let areaMeasurementDerivedCache = new WeakMap();

function numeric(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function point(value = {}) {
  return {
    x: numeric(value.x),
    z: numeric(value.z ?? value.y)
  };
}

function pointsEqual(a, b, tolerance = POINT_EQUALITY_TOLERANCE_MM) {
  return distance(point(a), point(b)) <= tolerance;
}

function normalizedAreaVertexRef(rawRef, fallback) {
  if (rawRef?.type === 'area-edge-intersection') {
    const edgeA = normalizeGeometryRef(rawRef.edgeA);
    const edgeB = normalizeGeometryRef(rawRef.edgeB);
    if (edgeA?.type === 'segment' && edgeB?.type === 'segment') {
      return {
        type: 'area-edge-intersection',
        edgeA,
        edgeB,
        fallbackPoint: point(rawRef.fallbackPoint || fallback),
        role: 'corner'
      };
    }
  }
  return normalizeGeometryRef(rawRef) || {
    type: 'free-point',
    fallbackPoint: fallback,
    role: 'free'
  };
}

function normalizedVertex(raw = {}) {
  const fallback = point(raw.point || raw);
  return {
    point: fallback,
    ref: normalizedAreaVertexRef(raw.ref, fallback)
  };
}

function geometryRefSourceKeys(rawRef = {}) {
  if (!rawRef || typeof rawRef !== 'object') return [];
  if (rawRef.type === 'area-edge-intersection') {
    return [...new Set([
      ...geometryRefSourceKeys(rawRef.edgeA),
      ...geometryRefSourceKeys(rawRef.edgeB)
    ])];
  }
  const system = String(rawRef.systemType || rawRef.systemId || '*').trim() || '*';
  return [...new Set([
    rawRef.entityId,
    rawRef.sourceId,
    rawRef.groupId
  ].map(value => String(value || '').trim()).filter(Boolean).map(id => `${system}:${id}`))];
}

export function areaMeasurementSourceKeys(measurement = {}) {
  const stored = Array.isArray(measurement.automaticSourceKeys)
    ? measurement.automaticSourceKeys.map(value => String(value || '').trim()).filter(Boolean)
    : [];
  if (stored.length) return [...new Set(stored)];
  return [...new Set((measurement.vertices || []).flatMap(vertex => geometryRefSourceKeys(vertex?.ref)))];
}

export function areaMeasurementBounds(rawPoints = []) {
  const points = rawPoints.map(point).filter(entry => Number.isFinite(entry.x) && Number.isFinite(entry.z));
  if (!points.length) return null;
  return {
    minX: Math.min(...points.map(entry => entry.x)),
    minZ: Math.min(...points.map(entry => entry.z)),
    maxX: Math.max(...points.map(entry => entry.x)),
    maxZ: Math.max(...points.map(entry => entry.z))
  };
}

function boundsIntersect(first, second) {
  if (!first || !second) return true;
  return first.maxX >= second.minX
    && first.minX <= second.maxX
    && first.maxZ >= second.minZ
    && first.minZ <= second.maxZ;
}

function sourceKeysOverlap(first = [], second = []) {
  const right = new Set(second);
  if (!first.length || !right.size) return false;
  for (const left of first) {
    const leftId = String(left).split(':').at(-1);
    for (const candidate of right) {
      if (left === candidate || (leftId && leftId === String(candidate).split(':').at(-1))) return true;
    }
  }
  return false;
}

export function invalidateAreaMeasurementDerivedCache(measurement = null) {
  if (measurement && typeof measurement === 'object') return areaMeasurementDerivedCache.delete(measurement);
  areaMeasurementDerivedCache = new WeakMap();
  return true;
}

export function invalidateAreaMeasurementDerivedCachesForGeometryChange(project = S.project, change = {}) {
  const sourceKeys = [...(change.sourceKeys || [])];
  const changedBounds = change.bounds || null;
  let invalidated = 0;
  for (const measurement of project?.areaMeasurements || []) {
    const cached = areaMeasurementDerivedCache.get(measurement);
    const measurementBounds = cached?.bounds
      || measurement.automaticBounds
      || areaMeasurementBounds((measurement.vertices || []).map(vertex => vertex.point));
    if (!sourceKeysOverlap(areaMeasurementSourceKeys(measurement), sourceKeys)
      && !boundsIntersect(measurementBounds, changedBounds)) continue;
    if (areaMeasurementDerivedCache.delete(measurement)) invalidated += 1;
  }
  return invalidated;
}

function areaMeasurementDerivedData(measurement, project = S.project) {
  const cached = areaMeasurementDerivedCache.get(measurement);
  if (cached
    && cached.vertices === measurement?.vertices
    && cached.updatedAt === measurement?.updatedAt
    && cached.project === project) return cached;
  const points = resolveAreaMeasurementPoints(measurement, project);
  const derived = {
    project,
    vertices: measurement?.vertices,
    updatedAt: measurement?.updatedAt,
    points,
    bounds: areaMeasurementBounds(points),
    labelPoint: polygonInteriorLabelPoint(points),
    areaSquareMeters: polygonAreaSquareMeters(points),
    edgeLengths: points.map((start, index) => distance(start, points[(index + 1) % points.length]))
  };
  areaMeasurementDerivedCache.set(measurement, derived);
  return derived;
}

function normalizedAreaBounds(rawBounds, fallbackPoints = []) {
  const minX = Number(rawBounds?.minX);
  const minZ = Number(rawBounds?.minZ);
  const maxX = Number(rawBounds?.maxX);
  const maxZ = Number(rawBounds?.maxZ);
  if ([minX, minZ, maxX, maxZ].every(Number.isFinite)) {
    return {
      minX: Math.min(minX, maxX),
      minZ: Math.min(minZ, maxZ),
      maxX: Math.max(minX, maxX),
      maxZ: Math.max(minZ, maxZ)
    };
  }
  return areaMeasurementBounds(fallbackPoints);
}

function deduplicatePolygonVertices(vertices = []) {
  const result = [];
  for (const raw of vertices) {
    const vertex = normalizedVertex(raw);
    if (!result.length || !pointsEqual(result[result.length - 1].point, vertex.point)) result.push(vertex);
  }
  if (result.length > 1 && pointsEqual(result[0].point, result[result.length - 1].point)) result.pop();
  return result;
}

export function polygonSignedAreaSquareMillimeters(points = []) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let doubleArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = point(points[index]);
    const next = point(points[(index + 1) % points.length]);
    doubleArea += current.x * next.z - next.x * current.z;
  }
  return doubleArea / 2;
}

export function polygonAreaSquareMeters(points = []) {
  return Math.abs(polygonSignedAreaSquareMillimeters(points)) / 1_000_000;
}

export function formatAreaSquareMeters(areaSquareMeters) {
  return `${Math.max(0, numeric(areaSquareMeters)).toFixed(2)} m²`;
}

export function pointInPolygon(candidate, points = []) {
  if (!Array.isArray(points) || points.length < 3) return false;
  const p = point(candidate);
  let inside = false;
  for (let index = 0, previous = points.length - 1; index < points.length; previous = index, index += 1) {
    const a = point(points[index]);
    const b = point(points[previous]);
    if (distancePointToSegment(p, a, b) <= 0.001) return true;
    const crosses = ((a.z > p.z) !== (b.z > p.z))
      && p.x < ((b.x - a.x) * (p.z - a.z)) / ((b.z - a.z) || Number.EPSILON) + a.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function polygonCentroid(points = []) {
  const signedArea = polygonSignedAreaSquareMillimeters(points);
  if (Math.abs(signedArea) < MIN_AREA_SQUARE_MM) {
    return points.reduce((sum, raw) => {
      const current = point(raw);
      return { x: sum.x + current.x / points.length, z: sum.z + current.z / points.length };
    }, { x: 0, z: 0 });
  }
  let x = 0;
  let z = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = point(points[index]);
    const next = point(points[(index + 1) % points.length]);
    const cross = current.x * next.z - next.x * current.z;
    x += (current.x + next.x) * cross;
    z += (current.z + next.z) * cross;
  }
  return { x: x / (6 * signedArea), z: z / (6 * signedArea) };
}

function polygonIsConvex(points = []) {
  if (points.length < 4) return true;
  let direction = 0;
  for (let index = 0; index < points.length; index += 1) {
    const a = point(points[index]);
    const b = point(points[(index + 1) % points.length]);
    const c = point(points[(index + 2) % points.length]);
    const cross = (b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x);
    if (Math.abs(cross) <= 0.000001) continue;
    const nextDirection = Math.sign(cross);
    if (direction && nextDirection !== direction) return false;
    direction = nextDirection;
  }
  return true;
}

function polygonEdgeDistance(candidate, points) {
  let closest = Infinity;
  for (let index = 0; index < points.length; index += 1) {
    closest = Math.min(
      closest,
      distancePointToSegment(candidate, points[index], points[(index + 1) % points.length])
    );
  }
  return closest;
}

// Liefert auch bei konkaven L-/U-Flächen einen tatsächlich innerhalb der
// Fläche liegenden Beschriftungspunkt. Der mathematische Schwerpunkt kann
// bei solchen Polygonen in der Aussparung liegen; eine kleine Raster- und
// Verfeinerungssuche maximiert stattdessen den Abstand zu allen Kanten.
export function polygonInteriorLabelPoint(rawPoints = []) {
  const points = rawPoints.map(point);
  if (points.length < 3) return points[0] || { x: 0, z: 0 };
  const xs = points.map(entry => entry.x);
  const zs = points.map(entry => entry.z);
  const bounds = {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minZ: Math.min(...zs),
    maxZ: Math.max(...zs)
  };
  const centroid = polygonCentroid(points);
  if (polygonIsConvex(points) && pointInPolygon(centroid, points)) return centroid;
  const candidates = [
    centroid,
    { x: (bounds.minX + bounds.maxX) / 2, z: (bounds.minZ + bounds.maxZ) / 2 }
  ].filter(candidate => pointInPolygon(candidate, points));
  let best = candidates[0] || points[0];
  let bestScore = pointInPolygon(best, points) ? polygonEdgeDistance(best, points) : -1;
  const divisions = 20;
  for (let row = 0; row < divisions; row += 1) {
    for (let column = 0; column < divisions; column += 1) {
      const candidate = {
        x: bounds.minX + ((column + 0.5) / divisions) * (bounds.maxX - bounds.minX),
        z: bounds.minZ + ((row + 0.5) / divisions) * (bounds.maxZ - bounds.minZ)
      };
      if (!pointInPolygon(candidate, points)) continue;
      const score = polygonEdgeDistance(candidate, points);
      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }
    }
  }
  let step = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) / divisions;
  for (let iteration = 0; iteration < 5; iteration += 1) {
    step /= 2;
    for (const dx of [-step, 0, step]) {
      for (const dz of [-step, 0, step]) {
        const candidate = { x: best.x + dx, z: best.z + dz };
        if (!pointInPolygon(candidate, points)) continue;
        const score = polygonEdgeDistance(candidate, points);
        if (score > bestScore) {
          best = candidate;
          bestScore = score;
        }
      }
    }
  }
  return best;
}

function orientation(a, b, c) {
  const value = (point(b).z - point(a).z) * (point(c).x - point(b).x)
    - (point(b).x - point(a).x) * (point(c).z - point(b).z);
  if (Math.abs(value) < 1e-7) return 0;
  return value > 0 ? 1 : -1;
}

function onSegment(a, b, c) {
  const pA = point(a);
  const pB = point(b);
  const pC = point(c);
  return pB.x <= Math.max(pA.x, pC.x) + 1e-7
    && pB.x >= Math.min(pA.x, pC.x) - 1e-7
    && pB.z <= Math.max(pA.z, pC.z) + 1e-7
    && pB.z >= Math.min(pA.z, pC.z) - 1e-7;
}

function segmentsIntersect(a, b, c, d) {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(a, c, b)) return true;
  if (o2 === 0 && onSegment(a, d, b)) return true;
  if (o3 === 0 && onSegment(c, a, d)) return true;
  return o4 === 0 && onSegment(c, b, d);
}

function cross2D(a, b) {
  return numeric(a.x) * numeric(b.z) - numeric(a.z) * numeric(b.x);
}

export function infiniteLineIntersection(lineA, lineB) {
  const a = point(lineA?.a);
  const b = point(lineA?.b);
  const c = point(lineB?.a);
  const d = point(lineB?.b);
  const directionA = { x: b.x - a.x, z: b.z - a.z };
  const directionB = { x: d.x - c.x, z: d.z - c.z };
  const denominator = cross2D(directionA, directionB);
  const scale = Math.max(1, Math.hypot(directionA.x, directionA.z) * Math.hypot(directionB.x, directionB.z));
  if (Math.abs(denominator) <= 1e-9 * scale) return null;
  const offset = { x: c.x - a.x, z: c.z - a.z };
  const t = cross2D(offset, directionB) / denominator;
  return { x: a.x + directionA.x * t, z: a.z + directionA.z * t };
}

function edgeRefWithoutAnchor(rawRef) {
  if (!rawRef || rawRef.type !== 'segment') return null;
  const { t: _t, anchorT: _anchorT, ...edgeRef } = rawRef;
  return normalizeGeometryRef(edgeRef);
}

function normalizedAreaConstraint(raw = {}) {
  const anchor = normalizedVertex(raw.anchor || raw);
  const edgeRef = edgeRefWithoutAnchor(raw.edgeRef || (raw.kind === 'edge' ? anchor.ref : null));
  return edgeRef
    ? { kind: 'edge', anchor, edgeRef }
    : { kind: 'point', anchor };
}

function constraintFromSnap(snapped = {}) {
  const anchor = normalizedVertex({ point: snapped.point, ref: snapped.ref });
  const edgeRef = snapped.candidate?.kind === 'segment'
    ? edgeRefWithoutAnchor(snapped.candidate.ref || snapped.ref)
    : null;
  return edgeRef
    ? { kind: 'edge', anchor, edgeRef }
    : { kind: 'point', anchor };
}

function snapAreaBoundaryGeometry(candidatePoint) {
  return snapToDimensionGeometry(candidatePoint, { preferEdge: true });
}

function snapAreaBoundaryClick(candidatePoint) {
  const hover = draftState(false)?.hover;
  const scale = Math.max(0.001, numeric(S.camera2D?.scale, 1));
  if (hover?.candidate?.kind === 'segment'
    && hover.point
    && distance(candidatePoint, hover.point) <= 16 / scale) {
    return hover;
  }
  return snapAreaBoundaryGeometry(candidatePoint);
}

function resolvedConstraintEdge(constraint, project = S.project) {
  if (constraint?.kind !== 'edge') return null;
  const resolved = resolveGeometryRef(constraint.edgeRef, project);
  if (resolved?.kind !== 'segment' || distance(resolved.a, resolved.b) <= POINT_EQUALITY_TOLERANCE_MM) return null;
  return { a: point(resolved.a), b: point(resolved.b), ref: resolved.ref || constraint.edgeRef };
}

function intersectionVertex(firstConstraint, secondConstraint, project = S.project) {
  const first = resolvedConstraintEdge(firstConstraint, project);
  const second = resolvedConstraintEdge(secondConstraint, project);
  if (!first || !second) return null;
  const intersection = infiniteLineIntersection(first, second);
  if (!intersection) return null;
  return normalizedVertex({
    point: intersection,
    ref: {
      type: 'area-edge-intersection',
      edgeA: first.ref,
      edgeB: second.ref,
      fallbackPoint: intersection,
      role: 'corner'
    }
  });
}

function edgeEndpointVertex(entry, endpointIndex) {
  const endpoint = endpointIndex === 0 ? entry.edge.a : entry.edge.b;
  return normalizedVertex({
    point: endpoint,
    ref: {
      ...entry.edge.ref,
      t: endpointIndex
    }
  });
}

function distancePointToInfiniteLine(candidate, edge) {
  const axis = {
    x: edge.b.x - edge.a.x,
    z: edge.b.z - edge.a.z
  };
  const length = Math.hypot(axis.x, axis.z);
  if (length <= POINT_EQUALITY_TOLERANCE_MM) return Infinity;
  return Math.abs(cross2D(axis, {
    x: point(candidate).x - edge.a.x,
    z: point(candidate).z - edge.a.z
  })) / length;
}

function resolvedIntersectionEdge(rawRef, project = S.project) {
  const resolved = resolveGeometryRef(rawRef, project);
  if (resolved?.kind !== 'segment' || distance(resolved.a, resolved.b) <= POINT_EQUALITY_TOLERANCE_MM) return null;
  return {
    a: point(resolved.a),
    b: point(resolved.b),
    ref: resolved.ref || normalizeGeometryRef(rawRef)
  };
}

function endpointVertexNearestTo(edge, candidate) {
  const endpointIndex = distance(edge.a, candidate) <= distance(edge.b, candidate) ? 0 : 1;
  return edgeEndpointVertex({ edge }, endpointIndex);
}

// Automatisch erkannte Raumkanten dürfen für den Flächenschluss bis zu ihrem
// rechnerischen Schnittpunkt verlängert werden. Wird genau so ein Eckpunkt
// gelöscht, soll die Fläche nicht einfach über die benachbarten Polygonpunkte
// geschlossen werden. Stattdessen endet jede Begrenzung wieder an ihrem realen
// Segmentende; die beiden Enden bilden gemeinsam die neue direkte Abschlusskante.
function verticesWithExtendedIntersectionRemoved(measurement, vertexIndex, project = S.project) {
  const vertex = measurement?.vertices?.[vertexIndex];
  if (vertex?.ref?.type !== 'area-edge-intersection') return null;
  const edgeA = resolvedIntersectionEdge(vertex.ref.edgeA, project);
  const edgeB = resolvedIntersectionEdge(vertex.ref.edgeB, project);
  if (!edgeA || !edgeB) return null;
  const intersection = infiniteLineIntersection(edgeA, edgeB)
    || point(vertex.ref.fallbackPoint || vertex.point);
  const isExtended = distancePointToSegment(intersection, edgeA.a, edgeA.b) > EDGE_CHAIN_JOIN_TOLERANCE_MM
    || distancePointToSegment(intersection, edgeB.a, edgeB.b) > EDGE_CHAIN_JOIN_TOLERANCE_MM;
  if (!isExtended) return null;

  const resolvedPoints = resolveAreaMeasurementPoints(measurement, project);
  const previousPoint = resolvedPoints[(vertexIndex - 1 + resolvedPoints.length) % resolvedPoints.length];
  const nextPoint = resolvedPoints[(vertexIndex + 1) % resolvedPoints.length];
  const directPairingDistance = distancePointToInfiniteLine(previousPoint, edgeA)
    + distancePointToInfiniteLine(nextPoint, edgeB);
  const swappedPairingDistance = distancePointToInfiniteLine(previousPoint, edgeB)
    + distancePointToInfiniteLine(nextPoint, edgeA);
  const incomingEdge = directPairingDistance <= swappedPairingDistance ? edgeA : edgeB;
  const outgoingEdge = directPairingDistance <= swappedPairingDistance ? edgeB : edgeA;
  const rawVertices = measurement.vertices.slice();
  rawVertices.splice(
    vertexIndex,
    1,
    endpointVertexNearestTo(incomingEdge, intersection),
    endpointVertexNearestTo(outgoingEdge, intersection)
  );
  return deduplicatePolygonVertices(rawVertices);
}

function finiteEdgeJunction(first, second, project = S.project) {
  const intersection = intersectionVertex(first.constraint, second.constraint, project);
  if (!intersection) return null;
  if (distancePointToSegment(intersection.point, first.edge.a, first.edge.b) > EDGE_CHAIN_JOIN_TOLERANCE_MM) return null;
  if (distancePointToSegment(intersection.point, second.edge.a, second.edge.b) > EDGE_CHAIN_JOIN_TOLERANCE_MM) return null;
  return intersection;
}

function junctionKey(firstIndex, secondIndex) {
  return firstIndex < secondIndex
    ? `${firstIndex}:${secondIndex}`
    : `${secondIndex}:${firstIndex}`;
}

function edgeEndpointAwayFromJunction(entry, junction) {
  return distance(entry.edge.a, junction.point) >= distance(entry.edge.b, junction.point)
    ? edgeEndpointVertex(entry, 0)
    : edgeEndpointVertex(entry, 1);
}

// Eine offene Folge angrenzender Kanten wird als Polygonzug verstanden:
// zwei Kanten + direkte Verbindung ihrer freien Enden = Dreieck,
// drei Kanten + Abschlusslinie = Viereck usw. Die Klickreihenfolge spielt
// keine Rolle; die Topologie der tatsächlichen Segmentanschlüsse bestimmt
// die Reihenfolge. Ein vollständig geschlossener Kantenring bleibt ebenfalls
// direkt als Polygon verwendbar.
function connectedEdgeChainVertices(constraints, project = S.project) {
  const entries = constraints.map((constraint, index) => ({
    constraint,
    index,
    edge: resolvedConstraintEdge(constraint, project)
  }));
  if (entries.some(entry => !entry.edge)) return [];
  if (entries.length === 1) return [edgeEndpointVertex(entries[0], 0), edgeEndpointVertex(entries[0], 1)];

  const adjacency = entries.map(() => []);
  const junctions = new Map();
  for (let first = 0; first < entries.length; first += 1) {
    for (let second = first + 1; second < entries.length; second += 1) {
      const junction = finiteEdgeJunction(entries[first], entries[second], project);
      if (!junction) continue;
      adjacency[first].push(second);
      adjacency[second].push(first);
      junctions.set(junctionKey(first, second), junction);
    }
  }
  const isOpenChain = adjacency.filter(neighbours => neighbours.length === 1).length === 2
    && adjacency.every(neighbours => neighbours.length >= 1 && neighbours.length <= 2);
  const isClosedChain = entries.length >= 3 && adjacency.every(neighbours => neighbours.length === 2);
  if (!isOpenChain && !isClosedChain) return [];

  const start = isOpenChain ? adjacency.findIndex(neighbours => neighbours.length === 1) : 0;
  const order = [];
  let previous = -1;
  let current = start;
  while (current >= 0 && !order.includes(current)) {
    order.push(current);
    let next = -1;
    for (const neighbourIndex of adjacency[current]) {
      if (neighbourIndex !== previous && !order.includes(neighbourIndex)) {
        next = neighbourIndex;
        break;
      }
    }
    previous = current;
    current = next;
  }
  if (order.length !== entries.length) return [];

  if (isClosedChain) {
    return deduplicatePolygonVertices(order.map((entryIndex, orderIndex) => {
      const nextIndex = order[(orderIndex + 1) % order.length];
      return junctions.get(junctionKey(entryIndex, nextIndex));
    }).filter(Boolean));
  }

  const firstJunction = junctions.get(junctionKey(order[0], order[1]));
  const lastJunction = junctions.get(junctionKey(order[order.length - 2], order[order.length - 1]));
  if (!firstJunction || !lastJunction) return [];
  const vertices = [edgeEndpointAwayFromJunction(entries[order[0]], firstJunction)];
  for (let index = 0; index < order.length - 1; index += 1) {
    const junction = junctions.get(junctionKey(order[index], order[index + 1]));
    if (junction) vertices.push(junction);
  }
  vertices.push(edgeEndpointAwayFromJunction(entries[order[order.length - 1]], lastJunction));
  return deduplicatePolygonVertices(vertices);
}

function allEdgeConstraintVertices(constraints, project = S.project) {
  const edges = constraints.map(constraint => ({
    constraint,
    edge: resolvedConstraintEdge(constraint, project),
    anchor: point(constraint.anchor.point)
  }));
  if (edges.some(entry => !entry.edge)) return [];
  const center = edges.reduce((sum, entry) => ({
    x: sum.x + entry.anchor.x / edges.length,
    z: sum.z + entry.anchor.z / edges.length
  }), { x: 0, z: 0 });
  const halfPlanes = edges.map(entry => {
    const direction = {
      x: entry.edge.b.x - entry.edge.a.x,
      z: entry.edge.b.z - entry.edge.a.z
    };
    return {
      ...entry,
      direction,
      centerSide: cross2D(direction, {
        x: center.x - entry.edge.a.x,
        z: center.z - entry.edge.a.z
      })
    };
  });
  const vertices = [];
  for (let first = 0; first < halfPlanes.length; first += 1) {
    for (let second = first + 1; second < halfPlanes.length; second += 1) {
      const intersection = intersectionVertex(
        halfPlanes[first].constraint,
        halfPlanes[second].constraint,
        project
      );
      if (!intersection) continue;
      const inside = halfPlanes.every(halfPlane => {
        const directionLength = Math.hypot(halfPlane.direction.x, halfPlane.direction.z);
        const sideTolerance = Math.max(1e-7, directionLength * 1e-7);
        if (Math.abs(halfPlane.centerSide) <= sideTolerance) return true;
        const side = cross2D(halfPlane.direction, {
          x: intersection.point.x - halfPlane.edge.a.x,
          z: intersection.point.z - halfPlane.edge.a.z
        });
        return halfPlane.centerSide > 0
          ? side >= -sideTolerance
          : side <= sideTolerance;
      });
      if (inside && !vertices.some(vertex => pointsEqual(vertex.point, intersection.point))) vertices.push(intersection);
    }
  }
  if (vertices.length < 3) return [];
  const vertexCenter = vertices.reduce((sum, vertex) => ({
    x: sum.x + vertex.point.x / vertices.length,
    z: sum.z + vertex.point.z / vertices.length
  }), { x: 0, z: 0 });
  return vertices.sort((left, right) => (
    Math.atan2(left.point.z - vertexCenter.z, left.point.x - vertexCenter.x)
    - Math.atan2(right.point.z - vertexCenter.z, right.point.x - vertexCenter.x)
  ));
}

// Wandelt eine beliebige Folge aus Punkt- und Kantenwahlen in Eckpunkte um.
// Nur-Kanten-Flächen werden unabhängig von der Klickreihenfolge aus den
// eingeschlossenen Halbräumen gebildet (z.B. links, rechts, vorne, hinten).
// In Mischformen ersetzt der Schnitt zweier aufeinanderfolgender Kanten den
// betreffenden Eckpunkt; alle explizit gewählten Punkte bleiben unverändert.
export function areaMeasurementVerticesFromConstraints(rawConstraints = [], project = S.project) {
  const constraints = rawConstraints.map(normalizedAreaConstraint);
  if (!constraints.length) return [];
  if (constraints.every(constraint => constraint.kind === 'edge')) {
    const chainVertices = connectedEdgeChainVertices(constraints, project);
    if (chainVertices.length >= 2) return chainVertices;
    if (constraints.length >= 3) return allEdgeConstraintVertices(constraints, project);
    return [];
  }
  return deduplicatePolygonVertices(constraints.map((constraint, index) => {
    const previous = constraints[(index - 1 + constraints.length) % constraints.length];
    if (constraint.kind === 'edge' && previous?.kind === 'edge') {
      return intersectionVertex(previous, constraint, project) || constraint.anchor;
    }
    return constraint.anchor;
  }));
}

export function polygonHasSelfIntersections(points = []) {
  const count = points.length;
  if (count < 4) return false;
  for (let first = 0; first < count; first += 1) {
    const firstNext = (first + 1) % count;
    for (let second = first + 1; second < count; second += 1) {
      const secondNext = (second + 1) % count;
      if (first === second || firstNext === second || secondNext === first) continue;
      if (first === 0 && secondNext === 0) continue;
      if (segmentsIntersect(points[first], points[firstNext], points[second], points[secondNext])) return true;
    }
  }
  return false;
}

export function normalizeAreaMeasurementEntity(raw = {}) {
  const vertices = deduplicatePolygonVertices(Array.isArray(raw.vertices) ? raw.vertices : []);
  const automatic = raw.automatic === true;
  const automaticSourceKeys = automatic
    ? areaMeasurementSourceKeys({
        vertices,
        automaticSourceKeys: raw.automaticSourceKeys
      })
    : [];
  const automaticBounds = automatic
    ? normalizedAreaBounds(raw.automaticBounds, vertices.map(vertex => vertex.point))
    : null;
  const automaticDetectionVersion = automatic
    ? Math.max(0, Math.round(numeric(raw.automaticDetectionVersion, 0)))
    : 0;
  return {
    id: String(raw.id || makeId('area_measurement')),
    type: 'area-measurement',
    vertices,
    ...(automatic ? {
      automatic: true,
      automaticSeed: point(raw.automaticSeed || polygonInteriorLabelPoint(vertices.map(vertex => vertex.point))),
      ...(automaticSourceKeys.length ? { automaticSourceKeys } : {}),
      ...(automaticBounds ? { automaticBounds } : {}),
      ...(automaticDetectionVersion ? { automaticDetectionVersion } : {})
    } : {}),
    precision: Math.max(0, Math.min(4, Math.round(numeric(raw.precision, 2)))),
    createdAt: numeric(raw.createdAt, Date.now()),
    updatedAt: numeric(raw.updatedAt, Date.now())
  };
}

function ensureAreaMeasurementState() {
  if (!S.project || typeof S.project !== 'object') S.project = {};
  if (!Array.isArray(S.project.areaMeasurements)) S.project.areaMeasurements = [];
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  return S.project.areaMeasurements;
}

function findAreaMeasurement(id) {
  return ensureAreaMeasurementState().find(measurement => measurement.id === id) || null;
}

function editState(create = false) {
  ensureAreaMeasurementState();
  const selectedId = S.selection?.areaMeasurementId || null;
  let state = S.ui.areaMeasurementEdit || null;
  if (state && state.measurementId !== selectedId) {
    S.ui.areaMeasurementEdit = null;
    state = null;
  }
  if (!state && create && selectedId) {
    state = {
      measurementId: selectedId,
      selectedVertexIndex: null,
      insertMode: false,
      hover: null
    };
    S.ui.areaMeasurementEdit = state;
  }
  return state;
}

function clearAreaMeasurementEditState() {
  if (S.ui) S.ui.areaMeasurementEdit = null;
}

export function resolveAreaMeasurementPoints(measurement, project = S.project, resolve = ref => resolveGeometryRef(ref, project)) {
  return (measurement?.vertices || []).map(vertex => {
    if (vertex.ref?.type === 'area-edge-intersection') {
      const edgeA = resolve(vertex.ref.edgeA);
      const edgeB = resolve(vertex.ref.edgeB);
      if (edgeA?.kind === 'segment' && edgeB?.kind === 'segment') {
        return infiniteLineIntersection(edgeA, edgeB) || point(vertex.ref.fallbackPoint || vertex.point);
      }
      return point(vertex.ref.fallbackPoint || vertex.point);
    }
    const resolved = resolve(vertex.ref);
    if (resolved?.kind === 'point') return point(resolved.point);
    if (resolved?.kind === 'segment') {
      const t = Number(vertex.ref?.t ?? vertex.ref?.anchorT);
      if (Number.isFinite(t)) {
        const clamped = Math.max(0, Math.min(1, t));
        return {
          x: resolved.a.x + (resolved.b.x - resolved.a.x) * clamped,
          z: resolved.a.z + (resolved.b.z - resolved.a.z) * clamped
        };
      }
      return closestPointOnSegment(vertex.point, resolved.a, resolved.b);
    }
    return point(vertex.point);
  });
}

export function findAreaMeasurementContainingPoint(candidate, project = S.project) {
  if (!project || typeof project !== 'object') return null;
  const target = point(candidate);
  const measurements = Array.isArray(project.areaMeasurements) ? project.areaMeasurements : [];
  for (const measurement of measurements) {
    const cached = areaMeasurementDerivedCache.get(measurement);
    const useCached = cached
      && cached.vertices === measurement?.vertices
      && cached.updatedAt === measurement?.updatedAt
      && cached.project === project;
    const points = useCached ? cached.points : resolveAreaMeasurementPoints(measurement, project);
    const bounds = useCached ? cached.bounds : areaMeasurementBounds(points);
    if (points.length < 3) continue;
    if (bounds && (
      target.x < bounds.minX
      || target.x > bounds.maxX
      || target.z < bounds.minZ
      || target.z > bounds.maxZ
    )) continue;
    if (pointInPolygon(target, points)) return measurement;
  }
  return null;
}

export function normalizeProjectAreaMeasurements(project = S.project) {
  if (!project || typeof project !== 'object') return [];
  project.areaMeasurements = Array.isArray(project.areaMeasurements)
    ? project.areaMeasurements
      .map(normalizeAreaMeasurementEntity)
      .filter(measurement => (
        measurement.vertices.length >= 3
        && Math.abs(polygonSignedAreaSquareMillimeters(measurement.vertices.map(vertex => vertex.point))) >= MIN_AREA_SQUARE_MM
      ))
    : [];
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (S.selection.areaMeasurementId
    && !project.areaMeasurements.some(measurement => measurement.id === S.selection.areaMeasurementId)) {
    S.selection.areaMeasurementId = null;
    clearAreaMeasurementEditState();
  }
  return project.areaMeasurements;
}

function draftState(create = false) {
  ensureAreaMeasurementState();
  if (!S.ui.areaMeasurementDraft && create) {
    S.ui.areaMeasurementDraft = { constraints: [], vertices: [], hover: null };
  }
  return S.ui.areaMeasurementDraft || null;
}

function setStatusText(text) {
  const element = document.getElementById('statusText') || document.getElementById('statusbar');
  if (element) element.textContent = text;
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.statusText = text;
}

export function isAreaMeasurementToolActive() {
  return S.ui?.activeTool?.owner === 'core' && S.ui.activeTool.tool === AREA_MEASUREMENT_TOOL;
}

export function syncAreaMeasurementToolbarButton() {
  const button = document.getElementById('btnAreaMeasurementTool');
  if (!button) return;
  const active = isAreaMeasurementToolActive();
  button.classList.toggle('is-active', active);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
}

export function activateAreaMeasurementTool() {
  ensureAreaMeasurementState();
  S.ui.activeTool = { owner: 'core', tool: AREA_MEASUREMENT_TOOL, meta: {} };
  S.ui.areaMeasurementDraft = { constraints: [], vertices: [], hover: null };
  setStatusText('Flächenmessung · mindestens drei Punkte oder Kanten anklicken');
  syncAreaMeasurementToolbarButton();
  return true;
}

export function deactivateAreaMeasurementTool() {
  if (isAreaMeasurementToolActive()) S.ui.activeTool = null;
  if (S.ui) S.ui.areaMeasurementDraft = null;
  syncAreaMeasurementToolbarButton();
  setStatusText('Flächenmessung deaktiviert');
  AppRuntime.requestPlanRender('area-measurement-tool-off');
  return true;
}

export function cancelAreaMeasurementInteraction() {
  const draft = draftState(false);
  if (draft?.constraints?.length || draft?.vertices?.length) {
    S.ui.areaMeasurementDraft = { constraints: [], vertices: [], hover: null };
    setStatusText('Flächenmessung abgebrochen');
    AppRuntime.requestPlanRender('area-measurement-cancel', { kind: 'annotations' });
    return true;
  }
  if (isAreaMeasurementToolActive()) {
    S.ui.activeTool = null;
    S.ui.areaMeasurementDraft = null;
    syncAreaMeasurementToolbarButton();
    setStatusText('Flächenmessung deaktiviert');
    return true;
  }
  const edit = editState(false);
  if (edit?.insertMode || Number.isInteger(edit?.selectedVertexIndex)) {
    edit.insertMode = false;
    edit.selectedVertexIndex = null;
    edit.hover = null;
    setStatusText('Flächenpunkt-Bearbeitung beendet');
    AppRuntime.requestPlanRender('area-measurement-edit-cancel');
    return true;
  }
  if (S.selection?.areaMeasurementId) {
    S.selection.areaMeasurementId = null;
    clearAreaMeasurementEditState();
    setStatusText('Auswahl aufgehoben');
    return true;
  }
  return false;
}

export function addAreaMeasurementDraftVertex(snapped) {
  const draft = draftState(true);
  if (!Array.isArray(draft.constraints)) {
    draft.constraints = (draft.vertices || []).map(vertex => ({ kind: 'point', anchor: vertex }));
  }
  const constraint = constraintFromSnap(snapped);
  const previous = draft.constraints[draft.constraints.length - 1];
  if (previous
    && previous.kind === constraint.kind
    && pointsEqual(previous.anchor.point, constraint.anchor.point)
    && (constraint.kind !== 'edge'
      || JSON.stringify(previous.edgeRef) === JSON.stringify(constraint.edgeRef))) return false;
  draft.constraints.push(constraint);
  draft.vertices = areaMeasurementVerticesFromConstraints(draft.constraints);
  draft.hover = null;
  const count = draft.constraints.length;
  const selectionLabel = constraint.kind === 'edge' ? 'Kante' : 'Punkt';
  setStatusText(draft.vertices.length >= 3
    ? 'Flächenmessung · weitere angrenzende Kanten oder Punkte anklicken oder mit dem grünen Haken abschließen'
    : count === 1 && constraint.kind === 'edge'
      ? 'Flächenmessung · Kante übernommen, eine angrenzende Kante für das Dreieck anklicken'
      : `${selectionLabel} übernommen · weitere angrenzende Kante oder einen Punkt anklicken`);
  AppRuntime.requestPlanRender('area-measurement-point', { kind: 'annotations' });
  return true;
}

export function finishAreaMeasurementDraft() {
  const draft = draftState(false);
  if (!draft || draft.vertices.length < 3) {
    setStatusText('Flächenmessung benötigt drei Eckpunkte oder mindestens zwei angrenzende Kanten');
    return false;
  }
  const measurement = createAreaMeasurementFromVertices(draft.vertices, {
    undoLabel: 'Flächenmessung setzen',
    refreshReason: 'area-measurement-create',
    successPrefix: 'Flächenmessung gesetzt'
  });
  if (!measurement) return false;
  S.ui.areaMeasurementDraft = { constraints: [], vertices: [], hover: null };
  return measurement;
}

export function createAreaMeasurementFromVertices(rawVertices = [], options = {}) {
  const measurement = normalizeAreaMeasurementEntity({
    vertices: rawVertices,
    automatic: options.automatic === true,
    automaticSeed: options.automaticSeed,
    automaticSourceKeys: options.automaticSourceKeys,
    automaticBounds: options.automaticBounds,
    automaticDetectionVersion: options.automaticDetectionVersion
  });
  const points = resolveAreaMeasurementPoints(measurement);
  if (points.length < 3) {
    setStatusText(options.invalidPointText || 'Flächenmessung benötigt mindestens drei Eckpunkte');
    return false;
  }
  if (polygonHasSelfIntersections(points)) {
    setStatusText(options.selfIntersectionText
      || 'Flächenmessung kann nicht abgeschlossen werden: Kanten dürfen sich nicht kreuzen');
    return false;
  }
  if (Math.abs(polygonSignedAreaSquareMillimeters(points)) < MIN_AREA_SQUARE_MM) {
    setStatusText(options.emptyAreaText || 'Flächenmessung benötigt eine geschlossene Fläche');
    return false;
  }
  const occupiedPoint = options.automatic === true ? measurement.automaticSeed : null;
  if (occupiedPoint && findAreaMeasurementContainingPoint(occupiedPoint)) {
    setStatusText(options.alreadyMeasuredText
      || 'Fläche bereits bemasst · bitte eine unbemasste Fläche wählen');
    return false;
  }
  recordUndo(options.undoLabel || 'Flächenmessung setzen');
  ensureAreaMeasurementState().push(measurement);
  S.selection.areaMeasurementId = measurement.id;
  S.selection.dimensionId = null;
  S.selection.textAnnotationId = null;
  AppRuntime.clearMultiSelection();
  if (S.file) S.file.dirty = true;
  setStatusText(`${options.successPrefix || 'Flächenmessung gesetzt'} · ${formatAreaSquareMeters(polygonAreaSquareMeters(points))}`);
  AppRuntime.requestAppRefresh(false, options.refreshReason || 'area-measurement-create');
  return measurement;
}

function detachAutomaticAreaMeasurement(measurement) {
  if (!measurement?.automatic) return;
  delete measurement.automatic;
  delete measurement.automaticSeed;
  delete measurement.automaticSourceKeys;
  delete measurement.automaticBounds;
  delete measurement.automaticDetectionVersion;
}

export function clearAreaMeasurementSelection() {
  if (!S.selection?.areaMeasurementId) return false;
  S.selection.areaMeasurementId = null;
  clearAreaMeasurementEditState();
  return true;
}

export function selectAreaMeasurement(id) {
  if (!findAreaMeasurement(id)) return false;
  const changed = S.selection.areaMeasurementId !== id;
  S.selection.areaMeasurementId = id;
  S.selection.dimensionId = null;
  S.selection.textAnnotationId = null;
  if (changed || S.ui?.areaMeasurementEdit?.measurementId !== id) {
    S.ui.areaMeasurementEdit = {
      measurementId: id,
      selectedVertexIndex: null,
      insertMode: false,
      hover: null
    };
  }
  AppRuntime.clearMultiSelection();
  setStatusText('Flächenmessung markiert');
  AppRuntime.requestPlanRender('area-measurement-select');
  return true;
}

export function deleteSelectedAreaMeasurement() {
  const id = S.selection?.areaMeasurementId;
  if (!id) return false;
  const edit = editState(false);
  if (Number.isInteger(edit?.selectedVertexIndex)) {
    const measurement = findAreaMeasurement(id);
    if (!measurement) return false;
    const index = edit.selectedVertexIndex;
    if (index < 0 || index >= measurement.vertices.length) {
      edit.selectedVertexIndex = null;
      return true;
    }
    const endpointReplacement = verticesWithExtendedIntersectionRemoved(measurement, index);
    if (!endpointReplacement && measurement.vertices.length <= 3) {
      setStatusText('Eine Flächenmessung benötigt mindestens drei Fangpunkte');
      return true;
    }
    const vertices = endpointReplacement
      || measurement.vertices.filter((_vertex, vertexIndex) => vertexIndex !== index);
    const points = resolveAreaMeasurementPoints({ ...measurement, vertices });
    if (vertices.length < 3
      || polygonHasSelfIntersections(points)
      || Math.abs(polygonSignedAreaSquareMillimeters(points)) < MIN_AREA_SQUARE_MM) {
      setStatusText('Dieser Fangpunkt kann nicht gelöscht werden, weil die Fläche dadurch ungültig würde');
      return true;
    }
    recordUndo(endpointReplacement ? 'Flächenecke an Kantenenden kürzen' : 'Flächenmesspunkt löschen');
    detachAutomaticAreaMeasurement(measurement);
    measurement.vertices = vertices;
    measurement.updatedAt = Date.now();
    edit.selectedVertexIndex = null;
    edit.insertMode = false;
    if (S.file) S.file.dirty = true;
    setStatusText(endpointReplacement
      ? `Flächenecke auf Kantenenden gekürzt · ${formatAreaSquareMeters(polygonAreaSquareMeters(points))}`
      : `Flächenmesspunkt gelöscht · ${formatAreaSquareMeters(polygonAreaSquareMeters(points))}`);
    AppRuntime.requestAppRefresh(false, 'area-measurement-vertex-delete');
    return true;
  }
  const measurements = ensureAreaMeasurementState();
  const index = measurements.findIndex(measurement => measurement.id === id);
  if (index < 0) return false;
  recordUndo('Flächenmessung löschen');
  measurements.splice(index, 1);
  S.selection.areaMeasurementId = null;
  clearAreaMeasurementEditState();
  if (S.file) S.file.dirty = true;
  setStatusText('Flächenmessung gelöscht');
  AppRuntime.requestAppRefresh(false, 'area-measurement-delete');
  return true;
}

function validEditedVertices(measurement, rawVertices) {
  const vertices = deduplicatePolygonVertices(rawVertices);
  if (vertices.length < 3 || vertices.length !== rawVertices.length) return null;
  const points = resolveAreaMeasurementPoints({ ...measurement, vertices });
  if (polygonHasSelfIntersections(points)) return null;
  if (Math.abs(polygonSignedAreaSquareMillimeters(points)) < MIN_AREA_SQUARE_MM) return null;
  return { vertices, points };
}

export function replaceAreaMeasurementVertex(measurementId, vertexIndex, snapped) {
  const measurement = findAreaMeasurement(measurementId);
  if (!measurement || vertexIndex < 0 || vertexIndex >= measurement.vertices.length) return false;
  const vertices = measurement.vertices.slice();
  vertices[vertexIndex] = normalizedVertex({ point: snapped?.point, ref: snapped?.ref });
  const valid = validEditedVertices(measurement, vertices);
  if (!valid) {
    setStatusText('Fangpunkt nicht übernommen: Die Fläche darf sich nicht kreuzen oder zusammenfallen');
    AppRuntime.requestPlanRender('area-measurement-vertex-invalid');
    return false;
  }
  recordUndo('Flächenmesspunkt verschieben');
  detachAutomaticAreaMeasurement(measurement);
  measurement.vertices = valid.vertices;
  measurement.updatedAt = Date.now();
  const edit = editState(true);
  if (edit) {
    edit.selectedVertexIndex = null;
    edit.insertMode = false;
    edit.hover = null;
  }
  if (S.file) S.file.dirty = true;
  setStatusText(`Flächenmesspunkt neu gesetzt · ${formatAreaSquareMeters(polygonAreaSquareMeters(valid.points))}`);
  AppRuntime.requestAppRefresh(false, 'area-measurement-vertex-move');
  return true;
}

function closestPolygonEdge(points, candidate) {
  let best = null;
  for (let index = 0; index < points.length; index += 1) {
    const a = points[index];
    const b = points[(index + 1) % points.length];
    const projected = closestPointOnSegment(candidate, a, b);
    const edgeDistance = distance(candidate, projected);
    if (!best || edgeDistance < best.distance) best = { index, point: projected, distance: edgeDistance };
  }
  return best;
}

export function insertAreaMeasurementVertexAtBoundary(measurementId, candidate, snappedOverride = null) {
  const measurement = findAreaMeasurement(measurementId);
  if (!measurement) return false;
  const points = resolveAreaMeasurementPoints(measurement);
  const edge = closestPolygonEdge(points, point(candidate));
  if (!edge) return false;
  const snapped = snappedOverride || snapToDimensionGeometry(edge.point);
  const vertex = normalizedVertex({ point: snapped?.point, ref: snapped?.ref });
  const nextIndex = (edge.index + 1) % measurement.vertices.length;
  if (pointsEqual(vertex.point, points[edge.index]) || pointsEqual(vertex.point, points[nextIndex])) {
    setStatusText('Bitte einen freien Zwischenpunkt auf der Flächenkante anklicken');
    return false;
  }
  const vertices = measurement.vertices.slice();
  const insertedIndex = edge.index + 1;
  vertices.splice(insertedIndex, 0, vertex);
  const valid = validEditedVertices(measurement, vertices);
  if (!valid) {
    setStatusText('Zwischenpunkt nicht übernommen: Die Fläche würde dadurch ungültig');
    return false;
  }
  recordUndo('Flächenmesspunkt einfügen');
  detachAutomaticAreaMeasurement(measurement);
  measurement.vertices = valid.vertices;
  measurement.updatedAt = Date.now();
  const edit = editState(true);
  if (edit) {
    edit.selectedVertexIndex = insertedIndex;
    edit.insertMode = false;
    edit.hover = null;
  }
  if (S.file) S.file.dirty = true;
  setStatusText(`Flächenmesspunkt eingefügt · ${formatAreaSquareMeters(polygonAreaSquareMeters(valid.points))}`);
  AppRuntime.requestAppRefresh(false, 'area-measurement-vertex-insert');
  return true;
}

function pointerWorld(event) {
  const svg = document.getElementById('planSvg');
  const rect = svg?.getBoundingClientRect?.();
  if (!rect) return null;
  const scale = numeric(S.camera2D?.scale, 1) || 1;
  return {
    x: (event.clientX - rect.left - rect.width / 2) / scale + numeric(S.camera2D?.x),
    z: (event.clientY - rect.top - rect.height / 2) / scale + numeric(S.camera2D?.z)
  };
}

function hoverSignature(hover) {
  if (!hover?.point) return '';
  const ref = hover.ref || hover.candidate?.ref || {};
  return [
    hover.role || '',
    ref.entityId || '',
    ref.type || '',
    ref.index ?? ref.corner ?? '',
    Math.round(numeric(hover.point.x) * 2) / 2,
    Math.round(numeric(hover.point.z) * 2) / 2
  ].join('|');
}

function targetData(target, selector, attribute, datasetKey) {
  const node = target?.closest?.(selector);
  if (!node) return null;
  return {
    node,
    value: node.getAttribute?.(attribute) || node.dataset?.[datasetKey] || null
  };
}

function startAreaMeasurementVertexDrag(event, measurementId, vertexIndex) {
  const measurement = findAreaMeasurement(measurementId);
  if (!measurement || vertexIndex < 0 || vertexIndex >= measurement.vertices.length) return false;
  selectAreaMeasurement(measurementId);
  const edit = editState(true);
  if (edit) {
    edit.insertMode = false;
    edit.hover = null;
  }
  S.drag = {
    ...(S.drag || {}),
    active: true,
    type: 'areaMeasurementVertex',
    measurementId,
    vertexIndex,
    startScreen: { x: numeric(event.clientX), y: numeric(event.clientY) },
    previewVertex: null,
    hasMoved: false
  };
  event.preventDefault?.();
  event.stopPropagation?.();
  return true;
}

export function handleAreaMeasurementPointerDown(event) {
  if (S.project?.currentView !== 'plan' || (Number.isFinite(event.button) && event.button !== 0)) return false;
  const finishTarget = event.target?.closest?.('[data-area-measurement-finish]');
  if (finishTarget && isAreaMeasurementToolActive()) {
    event.preventDefault?.();
    event.stopPropagation?.();
    finishAreaMeasurementDraft();
    return true;
  }
  const insertControl = targetData(
    event.target,
    '[data-area-measurement-insert]',
    'data-area-measurement-insert',
    'areaMeasurementInsert'
  );
  if (insertControl?.value && selectAreaMeasurement(insertControl.value)) {
    const edit = editState(true);
    edit.insertMode = !edit.insertMode;
    edit.selectedVertexIndex = null;
    edit.hover = null;
    setStatusText(edit.insertMode
      ? 'Zwischenpunkt einfügen · gewünschte Stelle auf der orangefarbenen Rahmenlinie anklicken'
      : 'Zwischenpunkt einfügen beendet');
    event.preventDefault?.();
    event.stopPropagation?.();
    AppRuntime.requestPlanRender('area-measurement-insert-toggle');
    return true;
  }
  const vertexTarget = targetData(
    event.target,
    '[data-area-measurement-vertex]',
    'data-area-measurement-id',
    'areaMeasurementId'
  );
  if (vertexTarget?.value) {
    const rawIndex = vertexTarget.node.getAttribute?.('data-area-measurement-vertex')
      ?? vertexTarget.node.dataset?.areaMeasurementVertex;
    const vertexIndex = Number(rawIndex);
    if (Number.isInteger(vertexIndex)) return startAreaMeasurementVertexDrag(event, vertexTarget.value, vertexIndex);
  }
  const measurementTarget = targetData(
    event.target,
    '[data-area-measurement-id]',
    'data-area-measurement-id',
    'areaMeasurementId'
  );
  const selectedEdit = editState(false);
  if (measurementTarget?.value
    && selectedEdit?.insertMode
    && selectedEdit.measurementId === measurementTarget.value) {
    const world = pointerWorld(event);
    if (!world) return false;
    event.preventDefault?.();
    event.stopPropagation?.();
    insertAreaMeasurementVertexAtBoundary(measurementTarget.value, world);
    return true;
  }
  const labelTarget = event.target?.closest?.('.area-measurement-text');
  if (measurementTarget?.value && labelTarget) {
    if (selectAreaMeasurement(measurementTarget.value)) {
      const edit = editState(true);
      edit.selectedVertexIndex = null;
      edit.insertMode = false;
      edit.hover = null;
      event.preventDefault?.();
      event.stopPropagation?.();
      AppRuntime.requestPlanRender('area-measurement-label-select');
      return true;
    }
  }
  if (!isAreaMeasurementToolActive()) return false;
  const world = pointerWorld(event);
  if (!world) return false;
  event.preventDefault?.();
  event.stopPropagation?.();
  addAreaMeasurementDraftVertex(snapAreaBoundaryClick(world));
  return true;
}

export function handleAreaMeasurementPointerMove(event) {
  if (S.project?.currentView !== 'plan') return false;
  const world = pointerWorld(event);
  if (!world) return false;
  if (S.drag?.active && S.drag.type === 'areaMeasurementVertex') {
    const screenDistance = Math.hypot(
      numeric(event.clientX) - numeric(S.drag.startScreen?.x),
      numeric(event.clientY) - numeric(S.drag.startScreen?.y)
    );
    if (!S.drag.hasMoved && screenDistance < VERTEX_DRAG_START_TOLERANCE_PX) return true;
    S.drag.hasMoved = true;
    S.drag.previewVertex = normalizedVertex(snapToDimensionGeometry(world));
    setStatusText('Flächenmesspunkt verschieben · am gewünschten Fangpunkt loslassen');
    AppRuntime.requestPlanRender('area-measurement-vertex-preview', { kind: 'annotations' });
    return true;
  }
  const edit = editState(false);
  if (edit?.insertMode) {
    const snapped = snapToDimensionGeometry(world);
    const previous = hoverSignature(edit.hover);
    edit.hover = snapped;
    if (previous !== hoverSignature(snapped)) AppRuntime.requestPlanRender('area-measurement-insert-preview', { kind: 'annotations' });
    return true;
  }
  if (!isAreaMeasurementToolActive()) return false;
  const draft = draftState(true);
  const snapped = snapAreaBoundaryGeometry(world);
  const previous = hoverSignature(draft.hover);
  draft.hover = snapped;
  if (previous !== hoverSignature(snapped)) AppRuntime.requestPlanRender('area-measurement-preview', { kind: 'annotations' });
  return true;
}

export function wantsAreaMeasurementPointerMoveFrame() {
  return isAreaMeasurementToolActive()
    || S.drag?.type === 'areaMeasurementVertex'
    || Boolean(editState(false)?.insertMode);
}

export function handleAreaMeasurementPointerUp(event = {}) {
  if (!(S.drag?.active && S.drag.type === 'areaMeasurementVertex')) return false;
  const measurementId = S.drag.measurementId;
  const vertexIndex = S.drag.vertexIndex;
  const moved = S.drag.hasMoved === true;
  const previewVertex = S.drag.previewVertex;
  const cancelled = event.type === 'pointercancel';
  S.drag.active = false;
  S.drag.type = null;
  S.drag.measurementId = null;
  S.drag.vertexIndex = null;
  S.drag.startScreen = null;
  S.drag.previewVertex = null;
  S.drag.hasMoved = false;
  const edit = editState(true);
  if (!cancelled && moved && previewVertex) {
    replaceAreaMeasurementVertex(measurementId, vertexIndex, previewVertex);
  } else if (!cancelled && edit) {
    edit.selectedVertexIndex = vertexIndex;
    edit.insertMode = false;
    edit.hover = null;
    setStatusText('Flächenmesspunkt markiert · mit Delete oder Backspace einzeln löschen');
    AppRuntime.requestPlanRender('area-measurement-vertex-select');
  } else {
    AppRuntime.requestPlanRender('area-measurement-vertex-cancel');
  }
  return true;
}

function screenPoint(worldPoint, worldToScreen) {
  const screen = worldToScreen(worldPoint.x, worldPoint.z);
  return { x: numeric(screen.x), y: numeric(screen.y) };
}

function readableAreaEdgeTextAngleDeg(a, b) {
  let angle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
  while (angle > 90) angle -= 180;
  while (angle <= -90) angle += 180;
  return angle;
}

// Die Beschriftung liegt exakt in der Längenmitte jeder Polygonkante. Der
// Versatz wird bewusst in Bildschirmpixeln berechnet, damit der Abstand bei
// jeder Zoomstufe lesbar bleibt. Die Polygonorientierung bestimmt, welche
// Normale in die Fläche zeigt – auch bei schrägen und konkaven Konturen.
export function areaMeasurementEdgeLabelLayouts(rawPoints = [], worldToScreen = null, options = {}) {
  if (typeof worldToScreen !== 'function') return [];
  const points = rawPoints.map(point);
  if (points.length < 3) return [];
  const insetPx = Math.max(0, numeric(options.insetPx, 16));
  const screenPoints = points.map(entry => screenPoint(entry, worldToScreen));
  const screenPolygon = screenPoints.map(entry => ({ x: entry.x, z: entry.y }));
  const clockwiseOnScreen = polygonSignedAreaSquareMillimeters(screenPoints) >= 0;
  return points.map((start, index) => {
    const end = points[(index + 1) % points.length];
    const a = screenPoints[index];
    const b = screenPoints[(index + 1) % screenPoints.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const screenLength = Math.hypot(dx, dy);
    if (screenLength <= Number.EPSILON
      || screenLength < Math.max(0, numeric(options.minScreenLengthPx))) return null;
    const inward = clockwiseOnScreen
      ? { x: -dy / screenLength, y: dx / screenLength }
      : { x: dy / screenLength, y: -dx / screenLength };
    const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    let appliedInsetPx = insetPx;
    let position = midpoint;
    while (appliedInsetPx >= 0.5) {
      position = {
        x: midpoint.x + inward.x * appliedInsetPx,
        y: midpoint.y + inward.y * appliedInsetPx
      };
      if (pointInPolygon({ x: position.x, z: position.y }, screenPolygon)) break;
      appliedInsetPx /= 2;
    }
    const length = Number.isFinite(Number(options.edgeLengths?.[index]))
      ? Number(options.edgeLengths[index])
      : distance(start, end);
    return {
      index,
      length,
      text: `${Math.round(length)} mm`,
      x: position.x,
      y: position.y,
      angle: readableAreaEdgeTextAngleDeg(a, b)
    };
  }).filter(Boolean);
}

function areaMeasurementPlanLabelMetrics() {
  const standard = resolvePlanLabelMetrics({
    cameraScale: S.camera2D?.scale,
    scaleMode: isPrintPlanCaptureInProgress()
      ? PLAN_LABEL_SCALE_MODES.SCREEN
      : PLAN_LABEL_SCALE_MODES.MODEL,
    screenFontSizePx: AREA_LABEL_REFERENCE_FONT_PX
  });
  // All measurement labels use the same global model-space font size.
  // The existing fixed-size print layout remains separate.
  const edgeMultiplier = standard.scaleMode === PLAN_LABEL_SCALE_MODES.SCREEN
    ? AREA_EDGE_LABEL_FONT_MULTIPLIER : 1;
  return {
    ...standard,
    edgeFontSizeMm: standard.fontSizeMm * edgeMultiplier,
    edgeFontSizePx: standard.fontSizePx * edgeMultiplier
  };
}

function renderAreaEdgeLabels(group, points, ctx, options = {}) {
  const labelMetrics = ctx.labelMetrics || areaMeasurementPlanLabelMetrics();
  const insetScaleFactor = labelMetrics.fontSizePx / AREA_LABEL_REFERENCE_FONT_PX;
  const insetPx = AREA_EDGE_LABEL_INSET_REFERENCE_PX * insetScaleFactor;
  for (const layout of areaMeasurementEdgeLabelLayouts(points, ctx.worldToScreen, { ...options, insetPx })) {
    const label = ctx.svgEl('text', {
      class: 'area-measurement-edge-text',
      x: layout.x,
      y: layout.y,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      transform: `rotate(${layout.angle} ${layout.x} ${layout.y})`,
      'data-plan-label-size': labelMetrics.size,
      'data-plan-label-scale-mode': labelMetrics.scaleMode,
      'font-size': labelMetrics.edgeFontSizePx
    }, group);
    label.textContent = layout.text;
  }
}

function renderAreaLabel(group, points, ctx, options = {}) {
  if (points.length < 3) return;
  const labelMetrics = ctx.labelMetrics || areaMeasurementPlanLabelMetrics();
  const labelWorld = options.labelPoint || polygonInteriorLabelPoint(points);
  const label = screenPoint(labelWorld, ctx.worldToScreen);
  const areaText = ctx.svgEl('text', {
    class: 'area-measurement-text',
    x: label.x,
    y: label.y,
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
    'data-plan-label-size': labelMetrics.size,
    'data-plan-label-scale-mode': labelMetrics.scaleMode,
    'font-size': labelMetrics.fontSizePx,
    ...(options.measurementId ? { 'data-area-measurement-id': options.measurementId } : {})
  }, group);
  areaText.textContent = formatAreaSquareMeters(
    Number.isFinite(Number(options.areaSquareMeters))
      ? Number(options.areaSquareMeters)
      : polygonAreaSquareMeters(points)
  );
  if (!options.draft && !options.selected) return;
  const buttonY = label.y - 27;
  if (options.selected && options.measurementId) {
    const insert = ctx.svgEl('g', {
      class: `area-measurement-insert${options.insertMode ? ' is-active' : ''}`,
      transform: `translate(${label.x - 14} ${buttonY - 12})`,
      'data-area-measurement-insert': options.measurementId,
      role: 'button',
      tabindex: '0',
      'aria-label': 'Zwischenpunkt auf der Flächenkante einfügen'
    }, group);
    ctx.svgEl('rect', {
      class: 'area-measurement-insert-bg',
      x: 0,
      y: 0,
      width: 28,
      height: 24,
      rx: 7
    }, insert);
    ctx.svgEl('circle', {
      class: 'area-measurement-insert-circle',
      cx: 14,
      cy: 12,
      r: 5
    }, insert);
    return;
  }
  const finish = ctx.svgEl('g', {
    class: 'area-measurement-finish',
    transform: `translate(${label.x - 14} ${buttonY - 12})`,
    'data-area-measurement-finish': 'true',
    role: 'button',
    tabindex: '0',
    'aria-label': 'Flächenmessung abschließen'
  }, group);
  ctx.svgEl('rect', {
    class: 'area-measurement-finish-bg',
    x: 0,
    y: 0,
    width: 28,
    height: 24,
    rx: 7
  }, finish);
  ctx.svgEl('path', {
    class: 'area-measurement-finish-check',
    d: 'M7 12.5l4 4L21 7.5'
  }, finish);
}

function measurementRenderData(measurement) {
  const derived = areaMeasurementDerivedData(measurement);
  const points = derived.points.slice();
  if (S.drag?.active
    && S.drag.type === 'areaMeasurementVertex'
    && S.drag.measurementId === measurement.id
    && S.drag.previewVertex?.point
    && Number.isInteger(S.drag.vertexIndex)
    && S.drag.vertexIndex >= 0
    && S.drag.vertexIndex < points.length) {
    points[S.drag.vertexIndex] = point(S.drag.previewVertex.point);
    return {
      ...derived,
      points,
      bounds: areaMeasurementBounds(points),
      labelPoint: polygonInteriorLabelPoint(points),
      areaSquareMeters: polygonAreaSquareMeters(points),
      edgeLengths: points.map((start, index) => distance(start, points[(index + 1) % points.length]))
    };
  }
  return derived;
}

function areaViewportBounds(ctx = {}) {
  if (typeof ctx.screenToWorld !== 'function'
    || !(Number(ctx.viewportWidth) > 0)
    || !(Number(ctx.viewportHeight) > 0)) return null;
  const topLeft = ctx.screenToWorld(-AREA_VIEWPORT_MARGIN_PX, -AREA_VIEWPORT_MARGIN_PX);
  const bottomRight = ctx.screenToWorld(
    Number(ctx.viewportWidth) + AREA_VIEWPORT_MARGIN_PX,
    Number(ctx.viewportHeight) + AREA_VIEWPORT_MARGIN_PX
  );
  return {
    minX: Math.min(topLeft.x, bottomRight.x),
    minZ: Math.min(topLeft.z, bottomRight.z),
    maxX: Math.max(topLeft.x, bottomRight.x),
    maxZ: Math.max(topLeft.z, bottomRight.z)
  };
}

function areaMeasurementLod(derived, ctx, options = {}) {
  if (options.selected || options.draft) return { showAreaLabel: true, showEdgeLabels: true };
  const bounds = derived?.bounds;
  if (!bounds) return { showAreaLabel: true, showEdgeLabels: true };
  const min = screenPoint({ x: bounds.minX, z: bounds.minZ }, ctx.worldToScreen);
  const max = screenPoint({ x: bounds.maxX, z: bounds.maxZ }, ctx.worldToScreen);
  const screenWidth = Math.abs(max.x - min.x);
  const screenHeight = Math.abs(max.y - min.y);
  return {
    showAreaLabel: Math.max(screenWidth, screenHeight) >= AREA_LABEL_MIN_SCREEN_SIZE_PX,
    showEdgeLabels: Math.max(screenWidth, screenHeight) >= AREA_EDGE_LABEL_MIN_SCREEN_LENGTH_PX
  };
}

function renderMeasurementPolygon(layer, measurement, ctx, options = {}) {
  const derived = options.derived || (options.points
    ? {
        points: options.points,
        bounds: areaMeasurementBounds(options.points),
        labelPoint: polygonInteriorLabelPoint(options.points),
        areaSquareMeters: polygonAreaSquareMeters(options.points),
        edgeLengths: options.points.map((start, index) => distance(start, options.points[(index + 1) % options.points.length]))
      }
    : measurementRenderData(measurement));
  const points = derived.points;
  if (points.length < 3) return null;
  if (!options.draft && !boundsIntersect(derived.bounds, options.viewportBounds)) return null;
  const lod = areaMeasurementLod(derived, ctx, options);
  const screenPoints = points.map(entry => screenPoint(entry, ctx.worldToScreen));
  const group = ctx.svgEl('g', {
    class: `area-measurement${options.draft ? ' is-draft' : ''}${options.selected ? ' is-selected' : ''}`,
    ...(measurement?.id ? { 'data-area-measurement-id': measurement.id } : {})
  }, layer);
  ctx.svgEl('polygon', {
    class: 'area-measurement-shape',
    points: screenPoints.map(entry => `${entry.x},${entry.y}`).join(' '),
    ...(measurement?.id ? { 'data-area-measurement-id': measurement.id } : {})
  }, group);
  if (measurement?.id && !options.draft && options.selected && options.insertMode) {
    ctx.svgEl('polygon', {
      class: 'area-measurement-hit',
      points: screenPoints.map(entry => `${entry.x},${entry.y}`).join(' '),
      'data-area-measurement-id': measurement.id
    }, group);
  }
  if (lod.showEdgeLabels) {
    renderAreaEdgeLabels(group, points, ctx, {
      edgeLengths: derived.edgeLengths,
      minScreenLengthPx: options.selected || options.draft ? 0 : AREA_EDGE_LABEL_MIN_SCREEN_LENGTH_PX
    });
  }
  if (lod.showAreaLabel) {
    renderAreaLabel(group, points, ctx, {
      ...options,
      labelPoint: derived.labelPoint,
      areaSquareMeters: derived.areaSquareMeters,
      measurementId: measurement?.id || null
    });
  }
  if (measurement?.id && options.selected && !options.draft) {
    const edit = editState(true);
    const screenVertices = points.map(entry => screenPoint(entry, ctx.worldToScreen));
    screenVertices.forEach((entry, index) => {
      ctx.svgEl('circle', {
        class: `area-measurement-vertex is-editable${edit?.selectedVertexIndex === index ? ' is-active' : ''}`,
        cx: entry.x,
        cy: entry.y,
        r: 5,
        'data-area-measurement-id': measurement.id,
        'data-area-measurement-vertex': index
      }, group);
    });
  }
  return group;
}

function renderDraft(layer, ctx) {
  const draft = draftState(false);
  if (!draft?.vertices?.length && !draft?.constraints?.length) return;
  const points = draft.vertices.map(vertex => point(vertex.point));
  for (const constraint of draft.constraints || []) {
    const edge = resolvedConstraintEdge(constraint);
    if (!edge) continue;
    const a = screenPoint(edge.a, ctx.worldToScreen);
    const b = screenPoint(edge.b, ctx.worldToScreen);
    ctx.svgEl('line', {
      class: 'area-measurement-selected-edge',
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y
    }, layer);
  }
  if (points.length >= 3) renderMeasurementPolygon(layer, null, ctx, { draft: true, points });
  const screenPoints = points.map(entry => screenPoint(entry, ctx.worldToScreen));
  const hoverPoint = draft.hover?.point ? point(draft.hover.point) : null;
  const previewPoints = hoverPoint ? [...points, hoverPoint] : points;
  if (previewPoints.length >= 2) {
    const previewScreen = previewPoints.map(entry => screenPoint(entry, ctx.worldToScreen));
    ctx.svgEl('polyline', {
      class: 'area-measurement-draft-line',
      points: previewScreen.map(entry => `${entry.x},${entry.y}`).join(' ')
    }, layer);
    if (points.length >= 2 && hoverPoint) {
      const first = screenPoints[0];
      const hover = previewScreen[previewScreen.length - 1];
      ctx.svgEl('line', {
        class: 'area-measurement-draft-close-line',
        x1: hover.x,
        y1: hover.y,
        x2: first.x,
        y2: first.y
      }, layer);
    }
  }
  for (const entry of screenPoints) {
    ctx.svgEl('circle', {
      class: 'area-measurement-vertex',
      cx: entry.x,
      cy: entry.y,
      r: 4
    }, layer);
  }
}

export function renderAreaMeasurements2D(parent, ctx = {}) {
  if (!parent || typeof ctx.svgEl !== 'function' || typeof ctx.worldToScreen !== 'function') return;
  const layer = ctx.svgEl('g', { class: 'area-measurement-layer' }, parent);
  const renderCtx = { ...ctx, labelMetrics: ctx.labelMetrics || areaMeasurementPlanLabelMetrics() };
  const selectedId = S.selection?.areaMeasurementId || null;
  const selectedEdit = editState(false);
  const viewportBounds = areaViewportBounds(renderCtx);
  for (const measurement of ensureAreaMeasurementState()) {
    const selected = measurement.id === selectedId;
    if (!selected
      && measurement.automaticBounds
      && !boundsIntersect(normalizedAreaBounds(measurement.automaticBounds), viewportBounds)) continue;
    renderMeasurementPolygon(layer, measurement, renderCtx, {
      selected,
      insertMode: selected && selectedEdit?.insertMode === true,
      viewportBounds
    });
  }
  if (isAreaMeasurementToolActive()) {
    renderDraft(layer, renderCtx);
    const hover = draftState(false)?.hover || null;
    if (hover) renderDimensionSnapPreview(layer, renderCtx, hover);
  } else if (selectedEdit?.insertMode && selectedEdit.hover) {
    renderDimensionSnapPreview(layer, renderCtx, selectedEdit.hover);
  }
}

export { AREA_MEASUREMENT_TOOL };
